/**
 * Google-Adresskomponenten in die Felder einer Rechnungsanschrift zerlegen.
 *
 * Gebraucht überall dort, wo die Anschrift getrennt gespeichert wird — bei
 * Rechnungskunden ist das Pflicht (§ 14 Abs. 4 UStG: vollständiger Name und
 * Anschrift des Leistungsempfängers).
 *
 * Die neue Places API liefert `addressComponents` mit `longText`/`shortText`,
 * die alte mit `long_name`/`short_name` — beide Formen werden akzeptiert.
 */
const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    return Array.from(value);
  } catch {
    return [];
  }
};

const pick = (components, type, short = false) => {
  // Die neue Places API liefert addressComponents array-ÄHNLICH, aber nicht als
  // echtes Array (kein .find). Array.from deckt Arrays, iterierbare und
  // array-artige Objekte gleichermaßen ab.
  const hit = toArray(components).find((c) => (c.types || []).includes(type));
  if (!hit) return '';
  return short
    ? (hit.shortText || hit.short_name || '')
    : (hit.longText || hit.long_name || '');
};

/**
 * @returns {{street: string, zip: string, city: string, country: string}}
 * Leere Strings, wenn Google die jeweilige Komponente nicht kennt (z. B. bei
 * einem reinen Ortsnamen ohne Hausnummer) — der Nutzer ergänzt dann von Hand.
 */
export const parseAddressComponents = (components) => {
  const list = toArray(components);
  if (list.length === 0) {
    return { street: '', zip: '', city: '', country: '' };
  }

  const route = pick(list, 'route');
  const streetNumber = pick(list, 'street_number');
  // Ort: bevorzugt die Gemeinde, sonst der übergeordnete Ort (bei Ortsteilen
  // liefert Google `locality` teils gar nicht).
  const city =
    pick(list, 'locality') ||
    pick(list, 'postal_town') ||
    pick(list, 'administrative_area_level_3') ||
    pick(list, 'sublocality');

  return {
    street: [route, streetNumber].filter(Boolean).join(' '),
    zip: pick(list, 'postal_code'),
    city,
    country: pick(list, 'country')
  };
};

/**
 * Anschrift aus der formatierten Adresse ableiten — Rückfallweg, wenn Google
 * keine `addressComponents` mitliefert (ältere Places-Variante, eingeschränkter
 * API-Key oder ein Vorschlagstyp ohne Komponenten).
 *
 * Erkennt das deutsche Format zuverlässig über das PLZ-Ort-Segment, auch wenn
 * ein Name vorangestellt ist:
 *   "Hotel Adlon, Unter den Linden 77, 10117 Berlin, Deutschland"
 *   "Musterstraße 12, 12345 Berlin"
 */
export const parseFormattedAddress = (formattedAddress) => {
  const empty = { street: '', zip: '', city: '', country: '' };
  if (!formattedAddress || typeof formattedAddress !== 'string') return empty;

  const segments = formattedAddress.split(',').map((s) => s.trim()).filter(Boolean);
  const zipIndex = segments.findIndex((s) => /^\d{4,5}\s+\S/.test(s));
  if (zipIndex === -1) return empty;

  const [, zip, city] = segments[zipIndex].match(/^(\d{4,5})\s+(.+)$/) || [];
  return {
    // Das Segment direkt vor „PLZ Ort" ist die Straße; alles davor ist ein
    // vorangestellter Name (Hotel, Firma) und gehört nicht in die Anschrift.
    street: zipIndex > 0 ? segments[zipIndex - 1] : '',
    zip: zip || '',
    city: city || '',
    country: zipIndex + 1 < segments.length ? segments[zipIndex + 1] : ''
  };
};

/**
 * Bevorzugt die strukturierten Komponenten, fällt sonst auf die formatierte
 * Adresse zurück. Das ist der Weg, den Formulare nutzen sollten.
 */
export const resolveAddressParts = (formattedAddress, components) => {
  const fromComponents = parseAddressComponents(components);
  if (fromComponents.zip && fromComponents.city) return fromComponents;

  const fromText = parseFormattedAddress(formattedAddress);
  // Einzelne Lücken aus dem jeweils anderen Weg schließen.
  return {
    street: fromComponents.street || fromText.street,
    zip: fromComponents.zip || fromText.zip,
    city: fromComponents.city || fromText.city,
    country: fromComponents.country || fromText.country
  };
};

export default parseAddressComponents;
